import { useState, useEffect } from "react";
import { useParams, Link } from 'react-router-dom';

import moment from "moment";

import Loading from "../../components/Loading";
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

function Existing() {
	const { id } = useParams();

	const [loading, setLoading] = useState(true);
	const [booking, setBooking] = useState(null);
	const [updating, setUpdating] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		fetchBooking();
	}, [id]);

	const fetchBooking = async () => {
		setLoading(true);
		try {
			const res = await Data.fetchServiceBooking(id);
			setBooking(res.data);
		} catch (err) {
			console.error('Error fetching booking:', err);
			setError('Booking not found');
		} finally {
			setLoading(false);
		}
	};

	const handleStatusChange = async (newStatus) => {
		if (newStatus === booking.status) return;

		const confirmMessage = `Are you sure you want to change the status to "${newStatus}"?`;
		if (!window.confirm(confirmMessage)) return;

		setUpdating(true);
		try {
			await Data.updateServiceBookingStatus(id, newStatus);
			await fetchBooking();
		} catch (err) {
			console.error('Error updating status:', err);
			alert(err.response?.data?.message || 'Failed to update status');
		} finally {
			setUpdating(false);
		}
	};

	const handleCancel = async () => {
		if (!window.confirm('Are you sure you want to cancel this booking?')) return;

		setUpdating(true);
		try {
			await Data.cancelServiceBooking(id, 'Cancelled by admin');
			await fetchBooking();
		} catch (err) {
			console.error('Error cancelling booking:', err);
			alert(err.response?.data?.message || 'Failed to cancel booking');
		} finally {
			setUpdating(false);
		}
	};

	const formatPrice = (price) => {
		if (price === 0) return 'Free';
		return `£${(price / 100).toFixed(2)}`;
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'confirmed': return '#28a745';
			case 'pending': return '#ffc107';
			case 'cancelled': return '#dc3545';
			case 'completed': return '#17a2b8';
			case 'no_show': return '#6c757d';
			default: return '#666';
		}
	};

	if (loading) return <Loading button={false} />;
	if (error) return <div style={{ padding: '20px', color: '#dc3545' }}>{error}</div>;

	return (
		<>
			<div style={{ marginBottom: '20px' }}>
				<Link to="/admin/service-bookings" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
					&larr; Back to Service Bookings
				</Link>
			</div>

			{booking.paymentVerificationStatus === 'mismatch' && (
				<div style={{
					background: '#f8d7da',
					border: '1px solid #dc3545',
					borderRadius: '4px',
					padding: '12px 16px',
					marginBottom: '20px',
					color: '#721c24'
				}}>
					<strong>Payment not confirmed.</strong> This booking is marked as paid, but our automatic check against the payment provider
					{booking.paymentVerifiedAt ? ` on ${moment(booking.paymentVerifiedAt).format('Do MMMM')} at ${moment(booking.paymentVerifiedAt).format('HH:mm')}` : ''} found
					no matching successful payment.{booking.paymentVerificationReason ? ` ${booking.paymentVerificationReason}` : ''} Please confirm with the customer before treating this booking as paid.
				</div>
			)}

			<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
				<span
					style={{
						background: getStatusColor(booking.status),
						color: '#fff',
						padding: '6px 16px',
						borderRadius: '4px',
						fontSize: '14px',
						textTransform: 'uppercase',
						fontWeight: 'bold'
					}}
				>
					{booking.status}
				</span>
				<span style={{ color: '#666' }}>Ref: {booking.reference}</span>
				{booking.paymentVerificationStatus === 'mismatch' && (
					<span style={{
						background: '#dc3545',
						color: '#fff',
						padding: '4px 10px',
						borderRadius: '4px',
						fontSize: '12px',
						fontWeight: 'bold'
					}}>
						PAYMENT NOT CONFIRMED
					</span>
				)}
			</div>

			<div className={styles.form}>
				<div>
					<p>Customer</p>
					<input
						type="text"
						value={`${booking.User?.firstName} ${booking.User?.lastName} (${booking.User?.email})`}
						disabled
					/>
				</div>
				<div>
					<p>Service</p>
					<input type="text" value={booking.Service?.name} disabled />
				</div>
				<div>
					<p>Staff</p>
					<input
						type="text"
						value={`${booking.Staff?.firstName} ${booking.Staff?.lastName}${booking.Staff?.title ? ` (${booking.Staff.title})` : ''}`}
						disabled
					/>
				</div>
				<div>
					<p>Date</p>
					<input
						type="text"
						value={moment(booking.date).format('dddd, MMMM D, YYYY')}
						disabled
					/>
				</div>
				<div>
					<p>Time</p>
					<input
						type="text"
						value={`${booking.startTime} - ${booking.endTime}`}
						disabled
					/>
				</div>
				<div>
					<p>Duration</p>
					<input
						type="text"
						value={`${booking.Service?.duration} minutes`}
						disabled
					/>
				</div>
				<div>
					<p>Price</p>
					<input type="text" value={formatPrice(booking.price)} disabled />
				</div>
				<div>
					<p>Payment</p>
					<input
						type="text"
						value={booking.paymentStatus?.charAt(0).toUpperCase() + booking.paymentStatus?.slice(1)}
						disabled
					/>
				</div>
				{booking.customerNotes && (
					<div>
						<p>Customer Notes</p>
						<input type="text" value={booking.customerNotes} disabled />
					</div>
				)}
				{booking.staffNotes && (
					<div>
						<p>Staff Notes</p>
						<input type="text" value={booking.staffNotes} disabled />
					</div>
				)}
				{booking.cancellationReason && (
					<div>
						<p>Cancellation Reason</p>
						<input type="text" value={booking.cancellationReason} disabled />
					</div>
				)}
				<div>
					<p>Booked At</p>
					<input
						type="text"
						value={moment(booking.createdAt).format('YYYY-MM-DD HH:mm:ss')}
						disabled
					/>
				</div>
			</div>

			{/* Status Change Section */}
			<div style={{
				background: '#fff',
				padding: '30px',
				borderRadius: '6px',
				marginTop: '20px'
			}}>
				<h3 style={{ margin: '0 0 20px 0', fontSize: '16px' }}>Change Status</h3>

				<div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
					{['pending', 'confirmed', 'completed', 'no_show', 'cancelled'].map((status) => (
						<button
							key={status}
							className="btn"
							disabled={updating || booking.status === status}
							onClick={() => handleStatusChange(status)}
							style={{
								background: booking.status === status ? getStatusColor(status) : '#f8f9fa',
								color: booking.status === status ? '#fff' : '#333',
								border: `2px solid ${getStatusColor(status)}`,
								padding: '10px 20px',
								borderRadius: '4px',
								cursor: booking.status === status ? 'default' : 'pointer',
								opacity: updating ? 0.6 : 1,
								textTransform: 'capitalize'
							}}
						>
							{status.replace('_', ' ')}
						</button>
					))}
				</div>
			</div>

			{/* Quick Actions */}
			{booking.status !== 'cancelled' && booking.status !== 'completed' && (
				<div className={styles.actions} style={{ marginTop: '20px' }}>
					<button
						className="btn"
						style={{ background: '#dc3545', color: '#fff', border: 'none' }}
						onClick={handleCancel}
						disabled={updating}
					>
						{updating ? 'Cancelling...' : 'Cancel Booking'}
					</button>
				</div>
			)}
		</>
	);
}

export default Existing;
